// pages/AdminHealth.jsx
// Dashboard de salud del sistema — solo admin. Refresh cada 3s.
// Datos en vivo de cada worker pm2 + sistema + Mongo + Redis.
import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';

const REFRESH_MS = 3000;
const CSS = `
.ah-root { padding: 16px 20px; max-width: 1400px; margin: 0 auto;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif; }
.ah-title { font-size: 22px; font-weight: 700; margin: 0 0 4px; }
.ah-sub { color: #6b7280; font-size: 13px; margin: 0 0 16px; }
.ah-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 12px; margin-bottom: 14px; }
.ah-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 14px 16px;
    box-shadow: 0 1px 2px rgba(15,23,42,.04); }
.ah-card h3 { margin: 0 0 10px; font-size: 13px; font-weight: 700; color: #0f172a;
    text-transform: uppercase; letter-spacing: .04em; }
.ah-row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 13px; color: #374151; }
.ah-row b { color: #0f172a; font-variant-numeric: tabular-nums; }
.ah-section { font-weight: 700; font-size: 15px; margin: 18px 0 8px; color: #0f172a; }
.ah-table { width: 100%; border-collapse: collapse; background: #fff; border: 1px solid #e5e7eb;
    border-radius: 10px; overflow: hidden; }
.ah-table th, .ah-table td { padding: 10px 12px; text-align: right; border-bottom: 1px solid #f1f5f9;
    font-size: 13px; font-variant-numeric: tabular-nums; }
.ah-table th:first-child, .ah-table td:first-child { text-align: left; }
.ah-table th { background: #f8fafc; font-weight: 700; color: #0f172a; font-size: 12px; text-transform: uppercase; letter-spacing: .04em; }
.ah-table tr:last-child td { border-bottom: none; }
.ah-status-ok { color: #059669; font-weight: 600; }
.ah-status-bad { color: #dc2626; font-weight: 600; }
.ah-bar { display: inline-block; height: 6px; border-radius: 999px; vertical-align: middle;
    background: linear-gradient(90deg, #22c55e, #eab308, #ef4444); position: relative; min-width: 60px; }
.ah-bar-fill { height: 100%; border-radius: 999px; background: #0f172a; opacity: .12; }
.ah-mini { font-size: 11px; color: #6b7280; }
.ah-pill { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 600; }
.ah-pill-ok { background: #d1fae5; color: #065f46; }
.ah-pill-warn { background: #fef3c7; color: #92400e; }
.ah-pill-bad { background: #fee2e2; color: #991b1b; }
`;

function fmtUptime(sec) {
    if (sec == null) return '—';
    const d = Math.floor(sec / 86400);
    const h = Math.floor((sec % 86400) / 3600);
    const m = Math.floor((sec % 3600) / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
}

function cpuPill(pct) {
    const cls = pct < 50 ? 'ah-pill-ok' : pct < 80 ? 'ah-pill-warn' : 'ah-pill-bad';
    return <span className={`ah-pill ${cls}`}>{pct.toFixed(1)}%</span>;
}

function memPill(pct) {
    const cls = pct < 60 ? 'ah-pill-ok' : pct < 80 ? 'ah-pill-warn' : 'ah-pill-bad';
    return <span className={`ah-pill ${cls}`}>{pct.toFixed(1)}%</span>;
}

export default function AdminHealth() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);
    const intervalRef = useRef(null);

    const fetchData = async () => {
        try {
            const r = await api.get('/admin/health');
            setData(r.data);
            setErr(null);
            setLastUpdate(new Date());
        } catch (e) {
            setErr(e.response?.data?.error || e.message);
        }
    };

    useEffect(() => {
        fetchData();
        intervalRef.current = setInterval(fetchData, REFRESH_MS);
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, []);

    if (!data && !err) {
        return <div style={{ padding: 24 }}>Cargando…</div>;
    }

    const sys = data?.system || {};
    const mongo = data?.mongo || {};
    const redis = data?.redis || {};
    const workers = data?.workers || [];

    return (
        <div className="ah-root">
            <style>{CSS}</style>
            <h1 className="ah-title">Salud del sistema</h1>
            <p className="ah-sub">
                Refresh cada {REFRESH_MS/1000}s. Última actualización: {lastUpdate ? lastUpdate.toLocaleTimeString() : '—'}
                {err && <span style={{ color: '#dc2626', marginLeft: 8 }}>(error: {err})</span>}
            </p>

            {/* === Workers === */}
            <div className="ah-section">Workers pm2 ({workers.length})</div>
            <table className="ah-table">
                <thead>
                    <tr>
                        <th>PID</th>
                        <th>Uptime</th>
                        <th>CPU</th>
                        <th>RSS</th>
                        <th>Heap</th>
                        <th>Event loop lag</th>
                        <th>Sockets</th>
                    </tr>
                </thead>
                <tbody>
                    {workers.length === 0 && (
                        <tr><td colSpan="7" style={{ textAlign: 'center', color: '#9ca3af' }}>Sin datos de workers</td></tr>
                    )}
                    {workers.map(w => (
                        <tr key={w.pid}>
                            <td>{w.pid}</td>
                            <td>{fmtUptime(w.uptime_sec)}</td>
                            <td>{cpuPill(w.cpu_pct ?? 0)}</td>
                            <td>{w.rss_mb} MB</td>
                            <td>{w.heap_used_mb} / {w.heap_total_mb} MB</td>
                            <td style={{ color: (w.event_loop_lag_ms || 0) > 50 ? '#dc2626' : '#059669' }}>
                                {w.event_loop_lag_ms || 0} ms
                            </td>
                            <td>{w.sockets ?? 0}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* === Sistema, Mongo, Redis en grid === */}
            <div className="ah-section">Sistema y dependencias</div>
            <div className="ah-grid">

                <div className="ah-card">
                    <h3>Sistema (EC2)</h3>
                    <div className="ah-row"><span>Hostname</span><b>{sys.hostname}</b></div>
                    <div className="ah-row"><span>Plataforma</span><b>{sys.platform}</b></div>
                    <div className="ah-row"><span>Uptime</span><b>{fmtUptime(sys.uptime_sec)}</b></div>
                    <div className="ah-row"><span>vCPUs</span><b>{sys.cpus}</b></div>
                    <div className="ah-row"><span>Load avg (1m / 5m / 15m)</span>
                        <b>{(sys.load_avg || []).map(n => n.toFixed(2)).join(' / ')}</b>
                    </div>
                    <div className="ah-row"><span>RAM total</span><b>{sys.total_mem_gb} GB</b></div>
                    <div className="ah-row"><span>RAM libre</span><b>{sys.free_mem_gb} GB</b></div>
                    <div className="ah-row"><span>RAM usada</span><b>{memPill(sys.mem_used_pct ?? 0)}</b></div>
                </div>

                <div className="ah-card">
                    <h3>MongoDB</h3>
                    <div className="ah-row"><span>Estado</span>
                        <b className={mongo.state === 'connected' ? 'ah-status-ok' : 'ah-status-bad'}>{mongo.state}</b>
                    </div>
                    {mongo.connections && (
                        <>
                            <div className="ah-row"><span>Conexiones actuales</span><b>{mongo.connections.current}</b></div>
                            <div className="ah-row"><span>Conexiones disponibles</span><b>{mongo.connections.available}</b></div>
                            <div className="ah-row"><span>Total creadas (lifetime)</span><b>{mongo.connections.totalCreated}</b></div>
                        </>
                    )}
                    {mongo.opcounters && (
                        <>
                            <div className="ah-row"><span>Queries</span><b>{mongo.opcounters.query.toLocaleString()}</b></div>
                            <div className="ah-row"><span>Inserts</span><b>{mongo.opcounters.insert.toLocaleString()}</b></div>
                            <div className="ah-row"><span>Updates</span><b>{mongo.opcounters.update.toLocaleString()}</b></div>
                            <div className="ah-row"><span>Deletes</span><b>{mongo.opcounters.delete.toLocaleString()}</b></div>
                        </>
                    )}
                </div>

                <div className="ah-card">
                    <h3>Redis</h3>
                    <div className="ah-row"><span>Estado</span>
                        <b className={redis.state === 'connected' ? 'ah-status-ok' : 'ah-status-bad'}>{redis.state}</b>
                    </div>
                    {redis.latencyMs != null && (
                        <div className="ah-row"><span>Latencia ping</span><b>{redis.latencyMs} ms</b></div>
                    )}
                    {redis.reason && (
                        <div className="ah-row"><span>Razón</span><b>{redis.reason}</b></div>
                    )}
                </div>
            </div>

            <p className="ah-mini" style={{ marginTop: 20 }}>
                Tip: si ves CPU sostenido &gt; 80% en ambos workers o event loop lag &gt; 100ms repetido,
                es momento de subir hardware (t3.xlarge + cluster 4) o investigar saturación específica.
            </p>
        </div>
    );
}
